import { fromGid } from '@/lib/shopify/gid';
import {
  ELEVAR_BRAND,
  ELEVAR_REFERRING_COLLECTION_KEY,
  ELEVAR_VIEW_CART_MAX_ITEMS,
} from '@/lib/elevar/constants';
import {
  findAttributeValue,
  findProductType,
  getCompareAtPriceAmount,
  getElevarCurrencyCode,
  getMoneyAmount,
  getUserData,
  initElevarDataLayer,
  resolveCartListPath,
  resolveElevarCategory,
} from '@/lib/elevar/shared';

/** Resolves currency for `dl_add_to_cart` from `{ variant, product }` payload items. */
const resolveAddToCartCurrencyCode = (cartPayloadQueue) => {
  const firstItem = cartPayloadQueue?.[0];
  if (!firstItem) return getElevarCurrencyCode(null);

  const source = firstItem.variant || firstItem.product?.variants?.[0];

  return getElevarCurrencyCode(source);
};

/** Cart lines may store the image URL in a `_productImage` custom attribute. */
const resolveProductImage = ({ attributes, variant, product, merchandise }) => {
  const imageFromAttributes = findAttributeValue(attributes, '_productImage');

  if (imageFromAttributes) return imageFromAttributes;

  return (
    variant?.image?.url ||
    merchandise?.image?.url ||
    product?.featuredImage?.url ||
    ''
  );
};

/** Required on `dl_add_to_cart` products per Elevar docs. */
export const buildProductUrl = (product, variant) => {
  if (!product?.handle || typeof window === 'undefined') return '';

  const variantQuery = variant?.id
    ? `?variant=${fromGid(variant.id)}`
    : '';

  return `${window.location.origin}/products/${product.handle}${variantQuery}`;
};

// Shopify cart line shape (`merchandise`). Options override list, quantity, or position.
const buildCartLineProduct = (cartItem, { index, quantity, list, pricing } = {}) => {
  const itemProductType = findProductType(cartItem?.attributes);
  const merchandise = cartItem?.merchandise;
  const { sale, full, hasPriceDifference } = pricing.variant(merchandise);

  const product = {
    id: merchandise?.sku || '',
    name: merchandise?.product?.title || '',
    brand: ELEVAR_BRAND,
    category: resolveElevarCategory(merchandise?.product),
    variant: merchandise?.title || '',
    price: getMoneyAmount(sale ?? merchandise?.price),
    compare_at_price: getCompareAtPriceAmount(hasPriceDifference, full, sale ?? merchandise?.price),
    list:
      list !== undefined
        ? list
        : resolveCartListPath(itemProductType || merchandise?.product?.productType),
    product_id: String(fromGid(merchandise?.product?.id || '')),
    variant_id: String(fromGid(merchandise?.id || '')),
    quantity: quantity !== undefined ? String(quantity) : String(cartItem?.quantity ?? 1),
    url: buildProductUrl(merchandise?.product, { id: merchandise?.id }),
    image: resolveProductImage({
      attributes: cartItem?.attributes,
      merchandise,
      product: merchandise?.product,
    }),
  };

  if (index !== undefined) {
    product.position = index + 1;
  }

  return product;
};

/** Pre-cart add payload from `AddToCartButton` (`{ variant, product, customAttributes }`). */
const buildAddToCartProduct = (cartItem, storedReferrer, pricing) => {
  const attributes = cartItem?.attributes || cartItem?.customAttributes;
  const product = cartItem?.product;
  const variant = cartItem?.variant;
  const { sale, full, hasPriceDifference } = pricing.variant(variant);

  return {
    id: variant?.sku || '',
    name: product?.title || '',
    brand: ELEVAR_BRAND,
    category: resolveElevarCategory(product),
    variant: variant?.title || '',
    price: getMoneyAmount(sale ?? variant?.price),
    compare_at_price: getCompareAtPriceAmount(hasPriceDifference, full, sale ?? variant?.price),
    list: storedReferrer || '',
    product_id: String(fromGid(product?.id || '')),
    variant_id: String(fromGid(variant?.id || '')),
    quantity: String(cartItem?.quantity ?? 1),
    image: resolveProductImage({ attributes, variant, product }),
    url: buildProductUrl(product, variant),
  };
};

/** Pushes `dl_user_data`. Must run before other page events on the same route. */
export async function elevarBaseData(cart, pricing) {
  if (!initElevarDataLayer()) return;

  const products = (cart?.lines || []).map((cartItem, index) =>
    buildCartLineProduct(cartItem, { index, pricing }),
  );

  const newEvent = {
    event: 'dl_user_data',
    cart_total: getMoneyAmount(
      pricing?.cart?.total(cart) ?? cart?.cost?.subtotalAmount,
    ),
    user_properties: getUserData(),
    ecommerce: {
      currencyCode: getElevarCurrencyCode(cart),
      cart_contents: {
        products,
      },
    },
  };

  window.ElevarDataLayer.push(newEvent);
}

/** Pushes `dl_view_cart`. Empty cart sends `impressions: []` and `cart_total: "0.00"`. Impressions capped by `ELEVAR_VIEW_CART_MAX_ITEMS`. */
export async function elevarViewCart(cart, pricing) {
  if (!initElevarDataLayer()) return;

  const products = [];

  (cart?.lines || []).forEach((cartItem, index) => {
    // Limit impressions array length to adhere to Elevar payload size recommendations
    if (products.length >= ELEVAR_VIEW_CART_MAX_ITEMS) return;

    products.push(buildCartLineProduct(cartItem, { index, pricing }));
  });

  window.ElevarDataLayer.push({
    event: 'dl_view_cart',
    user_properties: getUserData(),
    cart_total: getMoneyAmount(
      pricing?.cart?.total(cart) ?? cart?.cost?.subtotalAmount,
    ),
    ecommerce: {
      currencyCode: getElevarCurrencyCode(cart),
      actionField: {
        list: 'Shopping Cart',
      },
      impressions: products,
    },
  });
}

/** Pushes `dl_add_to_cart` for `{ variant, product }` payload items. */
export async function elevarAddToCart(cartPayloadQueue, pricing) {
  if (!initElevarDataLayer()) return;

  const storedReferrer =
    sessionStorage.getItem(ELEVAR_REFERRING_COLLECTION_KEY) || '';
  const products = [];

  cartPayloadQueue.forEach((cartItem) => {
    products.push(buildAddToCartProduct(cartItem, storedReferrer, pricing));
  });

  window.ElevarDataLayer.push({
    event: 'dl_add_to_cart',
    user_properties: getUserData(),
    ecommerce: {
      currencyCode: resolveAddToCartCurrencyCode(cartPayloadQueue),
      add: {
        actionField: {
          list: storedReferrer,
        },
        products,
      },
    },
  });
}

/**
 * Pushes `dl_remove_from_cart`.
 * Pass `quantity = 1` when cart quantity controls remove a single unit.
 */
export async function elevarRemoveFromCart(productsToRemove, quantity, pricing) {
  if (!initElevarDataLayer()) return;

  const storedReferrer =
    sessionStorage.getItem(ELEVAR_REFERRING_COLLECTION_KEY) || '';
  const products = productsToRemove.map((removedItem) =>
    buildCartLineProduct(removedItem, {
      list: storedReferrer,
      quantity: quantity === 1 ? quantity : undefined,
      pricing,
    }),
  );

  window.ElevarDataLayer.push({
    event: 'dl_remove_from_cart',
    user_properties: getUserData(),
    ecommerce: {
      currencyCode: getElevarCurrencyCode(productsToRemove?.[0]?.merchandise),
      remove: {
        actionField: {
          list: storedReferrer,
        },
        products,
      },
    },
  });
}
